/**
 * 63.14 step 6's start / DIRECTION §0.11 step B (2): settlements as module layouts on scored sites (ARCHITECTURE §5.22
 * "Settlements per seed"; DECISIONS 201 (4): hand-made layouts first; 205: flat sites first). **The planet's sites, from
 * the planet alone** (§0.11 (1): a pure function, cached per planet; nothing in the match state but the result): a keyed
 * scan of the planet's surface scored by the site score (core/siteScore.ts, flatness first) for a settlement's footprint,
 * the best SETTLEMENT.perPlanet of them spaced SETTLEMENT.spacingPeople apart; each a **lane** at a keyed yaw with
 * SETTLEMENT.lotsMin–lotsMax **lots** either side of it, each lot one of the ten hand-made layouts (core/modules.ts
 * TEAM_LAYOUTS, by builder index) facing the lane. Lane C's layout pass (step A, `placeOpenObjectives`) reads the list to
 * pick the match's objectives among them; `withSettlements` lays the lots of the settlements the board bounds as neutral
 * `house` structures at setup (behind `GameConfig.settlements`), which the setup's passes then settle (settleBuildings),
 * terrace (terraceBuilds, 2b), seat and stand like any building sized by function — drawn by their builder's model, or
 * its layout from the kit with modular buildings on, walked, fought over and broken as any modular building is. Off: no
 * structure, every byte as before. Not here: the far impostor and the held band at planet scale (§5.22 step 6's rest).
 *
 * 74.5 step 1 (DECISIONS 357, ARCHITECTURE §5.41 (1)–(2)): **sites by the world as well as the ground.** The world sheet
 * (core/worldSheet.ts, 74.3 step 0) adds three terms to the flat-first score, each in its units (a person of rise costs 1):
 * water near (hops to a drainage chain of order SETTLEMENT_WORLD.waterOrder and up, or to a lake's or a playa's edge), the
 * valley floor (the basin's lowness, as the sheet's wet mask reads it) and a crossing near (a ford, or a pass); a site in a
 * lake or on a playa is refused, and a crossing draws one site at most. Count and spacing are as before; only where the
 * sites stand moves. Each site's **kind** follows from where it stands (the five of 357: a ford village's lane crosses the
 * chain at its ford, a lakeshore hamlet's runs along the shore, a pass hamlet's through the col, a valley village's along
 * its chain's bank; a ridge post — high, dry, no water near — is one lot, a watch); the looks by kind and 72.5's masonry
 * are step 4. `SETTLEMENT_WORLD.on` off: the sites as before, bit for bit (the A/B's before arm).
 */
import type { GameState, PlanetState, StructureState, Vec3 } from '../types';
import { SeededRandom } from './random';
import { terraceSiteScore } from './siteScore';
import { buildingsByFunction, BUILDERS } from './buildingScale';
import { structureHealth } from './body';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { structureFrame, yawFacing } from './hits';
import { dacos, dcos, dsin } from './dmath';
import { vecNormalize, vecDot, vecSub, vecScale } from '../math';
import { nearestSurfaceVertex } from './surface';
import { worldSheetOf, type WorldSheet } from './worldSheet';

export const SETTLEMENT = {
  /** How many settlement sites a planet offers at least, and at most (the best of the scan, spaced): by its area, one per (2 × spacing)² between these. */
  perPlanetMin: 12,
  perPlanetMax: 48,
  /** The scan: this many keyed directions over the sphere. */
  candidates: 2000,
  /** Sites stand at least this far apart (people). */
  spacingPeople: 300,
  /** The footprint a site is scored for (half extent, people): the lane and its lots. */
  sitePeople: 16,
  /** Lots a settlement: between these. */
  lotsMin: 3,
  lotsMax: 5,
  /** The lane's half width (people): the lots' fronts stand this far from its line. */
  lanePeople: 4,
  /** The lots' pitch along the lane (people): the widest layout and a walk between. */
  pitchPeople: 14,
  /** The lots' centres stand this far from the lane's line (people): the lane's half width and half the deepest layout. */
  setbackPeople: 10,
  /** How many settlements a match lays at most (the board's region first, the best first). */
  maxPlaced: 6,
  /** With a board: a settlement lies within this share of the bases' separation of each base (the lens between them). */
  boardReach: 1.1,
} as const;

/**
 * 74.5 step 1: the world's terms in the site score (each a score in the site score's units: a person of rise costs 1), and
 * the kinds' reaches. `on`: the dial (off, the sites as before 74.5).
 */
export const SETTLEMENT_WORLD = {
  on: true,
  /** Water near: `water` at a chain's or a shore's vertex, nothing `waterHops` hops off; chains of `waterOrder` and up count (a stream, not a rill). */
  water: 4, waterHops: 4, waterOrder: 0.3,
  /** The valley floor: `lowness` × the basin's lowness (0 at its spill, 1 at its sink; the lowest basin by its elevation band). */
  lowness: 8,
  /** A crossing near (a ford or a pass of the sheet's candidates): `crossing` at it, nothing `crossingPeople` off; one site a crossing. */
  crossing: 4, crossingPeople: 200,
  /** The kinds: a ford village or a pass hamlet within `kindCrossingPeople` of its crossing; a lakeshore hamlet within `kindShoreHops` of a shore; a valley village's lane along a chain within `kindChainHops`; a ridge post at the elevation band `ridgeAt` and up with no water within `waterHops`. */
  kindCrossingPeople: 60, kindShoreHops: 2, kindChainHops: 3, ridgeAt: 0.6,
};

/** 74.5 (357): the five kinds a settlement is, by where it stands. */
export type SettlementKind = 'fordVillage' | 'lakeshoreHamlet' | 'passHamlet' | 'valleyVillage' | 'ridgePost';

/** One lot: the layout (its builder index, core/modules.ts teamLayout), where it stands, its yaw (its door toward the lane). */
export interface SettlementLot { builder: number; dir: Vec3; yaw: number }
/** A settlement site: its centre, its lane's yaw, its site score, its lots. */
export interface SettlementSite { id: number; dir: Vec3; yaw: number; score: number; lots: SettlementLot[]; /** 74.5: by the world (absent with SETTLEMENT_WORLD off) */ kind?: SettlementKind }

/** Per generated heightmap (the planet as made: `initialHeightmap` once anything has edited it), by person scale. */
const cache = new WeakMap<Float32Array, Map<string, readonly SettlementSite[]>>();

/** The angle between two unit directions. */
const arc = (a: Vec3, b: Vec3) => dacos(Math.max(-1, Math.min(1, vecDot(a, b))));

/**
 * The planet's settlement sites, from the planet alone (its id keys the scan's phase and each site's lots; the ground
 * scores them): pure, cached per planet object and person scale. Empty where the buildings are not sized by function.
 */
export function planetSettlements(state: Pick<GameState, 'planets' | 'config'>, planetIndex: number): readonly SettlementSite[] {
  const current = state.planets[planetIndex];
  if (!current || current.type !== 'planet' || !buildingsByFunction(state.config)) return [];
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
  // the planet as generated: its first heightmap, before any crater, dig, terrace or stand — so the sites are the same in every match on it, whenever asked
  // (74.19's yards: a carved world's sites are the ones its setup planned on, the ground before the carve)
  const generated = current.plannedHeightmap ?? current.initialHeightmap ?? current.heightmap;
  const planet: PlanetState = { ...current, heightmap: generated, initialHeightmap: undefined, digs: undefined, craterlets: undefined, terraces: undefined, stands: undefined, standCells: undefined };
  const scoreState = { planets: state.planets.map((p, i) => (i === planetIndex ? planet : p)), config: state.config };
  const world = SETTLEMENT_WORLD.on ? siteWorld(worldSheetOf(current.plannedHeightmap ? { ...current, heightmap: generated, initialHeightmap: generated } : current)) : null;
  const key = `${ps.k}|${ps.fineAmplitude}|${ps.fineWavelength}${world ? `|w${state.config.seed ?? ''}` : ''}`;
  let byKey = cache.get(generated);
  if (!byKey) { byKey = new Map(); cache.set(generated, byKey); }
  const hit = byKey.get(key);
  if (hit) return hit;
  const score = terraceSiteScore({ halfXPeople: SETTLEMENT.sitePeople, halfZPeople: SETTLEMENT.sitePeople });
  const rng = new SeededRandom(`${planet.id}-settlements`);
  const phase = rng.range(0, 2 * Math.PI), tilt = rng.range(-1, 1);
  // the scan: a Fibonacci sphere, its phase and its pole keyed by the planet, each direction scored
  const n = SETTLEMENT.candidates;
  const scored: Array<{ dir: Vec3; score: number; v: number; bonus: number; crossing: number }> = [];
  const ct = Math.sqrt(1 - tilt * tilt), reach = (SETTLEMENT_WORLD.crossingPeople * person) / planet.radius;
  for (let k = 0; k < n; k++) {
    const y = 1 - (2 * (k + 0.5)) / n, r = Math.sqrt(1 - y * y), a = k * 2.399963229728653 + phase;
    const x0 = r * dcos(a), z0 = r * dsin(a);
    // the pole turned about x by the keyed tilt (so no planet's sites sit on the axis the scan started from)
    const dir = { x: x0, y: y * ct - z0 * tilt, z: y * tilt + z0 * ct };
    if (!world) { scored.push({ dir, score: score(scoreState, planetIndex, dir), v: -1, bonus: 0, crossing: -1 }); continue; }
    // 74.5: the world's terms — refused in a lake or on a playa (its vertex or a neighbour), else water, lowness and the nearest crossing's draw
    const v = world.sheet.graph.nodeOf[nearestSurfaceVertex(current, dir)];
    if (world.wet[v]) continue;
    let base = score(scoreState, planetIndex, dir);
    base += SETTLEMENT_WORLD.water * Math.max(0, 1 - world.hops[v] / SETTLEMENT_WORLD.waterHops) + SETTLEMENT_WORLD.lowness * world.lowness[v];
    let crossing = -1, bonus = 0;
    for (let i = 0; i < world.crossings.length; i++) {
      const d = arc(dir, world.crossings[i].dir);
      if (d < reach && SETTLEMENT_WORLD.crossing * (1 - d / reach) > bonus) { bonus = SETTLEMENT_WORLD.crossing * (1 - d / reach); crossing = i; }
    }
    scored.push({ dir, score: base, v, bonus, crossing });
  }
  const order = (a: { dir: Vec3 }, b: { dir: Vec3 }) => a.dir.x - b.dir.x || a.dir.y - b.dir.y || a.dir.z - b.dir.z;
  scored.sort((a, b) => (b.score + b.bonus) - (a.score + a.bonus) || order(a, b));
  // the best, spaced (74.5: a crossing draws one site — a candidate whose crossing is taken counts without it)
  const spacing = (SETTLEMENT.spacingPeople * person) / planet.radius;
  const area = 4 * Math.PI * (planet.radius / person) * (planet.radius / person);
  const perPlanet = Math.max(SETTLEMENT.perPlanetMin, Math.min(SETTLEMENT.perPlanetMax, Math.round(area / (4 * SETTLEMENT.spacingPeople * SETTLEMENT.spacingPeople))));
  const sites: SettlementSite[] = [];
  const taken = new Set<number>(), used = new Uint8Array(scored.length);
  const value = (c: typeof scored[number]) => c.score + (c.crossing >= 0 && !taken.has(c.crossing) ? c.bonus : 0);
  for (let i = 0; i < scored.length && sites.length < perPlanet; i++) {
    // the best left: the list is sorted by its values before any crossing was taken; a taken crossing only lowers a value, so the best left is the first whose value is still its sorted one, or a lowered one above it
    let best = -1;
    for (let j = 0; j < scored.length; j++) {
      if (used[j]) continue;
      if (best >= 0 && scored[j].score + scored[j].bonus < value(scored[best])) break;   // (nothing past here can beat it)
      if (best < 0 || value(scored[j]) > value(scored[best]) || (value(scored[j]) === value(scored[best]) && order(scored[j], scored[best]) < 0)) best = j;
    }
    if (best < 0) break;
    used[best] = 1;
    const c = scored[best];
    if (sites.some(s => arc(s.dir, c.dir) < spacing)) continue;
    const id = sites.length;
    const own = c.crossing >= 0 && !taken.has(c.crossing) ? c.crossing : -1;
    if (own >= 0) taken.add(own);
    const kind = world ? siteKind(world, c.v, c.dir, own >= 0 ? world.crossings[own] : null, person / planet.radius) : null;
    const lots = settlementLots(planet, id, c.dir, person, kind);
    sites.push({ id, dir: c.dir, yaw: lots.yaw, score: value(c), lots: lots.lots, ...(kind ? { kind: kind.kind } : {}) });
  }
  byKey.set(key, sites);
  return sites;
}

/**
 * A settlement's lane and lots, keyed by the planet and the site: a yaw, 3–5 lots alternating either side of the lane, each a
 * layout facing it. 74.5: by its kind, the lane along `kind.lane` (a direction along the ground: the chain, the shore, the way
 * through the col) and a ridge post one lot.
 */
function settlementLots(planet: PlanetState, id: number, centre: Vec3, person: number, kind: { kind: SettlementKind; lane: Vec3 | null } | null): { yaw: number; lots: SettlementLot[] } {
  const rng = new SeededRandom(`${planet.id}-settlement-${id}`);
  const keyed = rng.range(0, 2 * Math.PI);
  const drawn = rng.int(SETTLEMENT.lotsMin, SETTLEMENT.lotsMax), count = kind?.kind === 'ridgePost' ? 1 : drawn;
  const up = vecNormalize(centre);
  // (the lane runs along the frame's tx: its yaw turns tx onto the lane's direction — yawFacing turns tz onto it, a quarter turn on)
  const yaw = kind?.lane ? yawFacing(up, kind.lane) + Math.PI / 2 : keyed;
  const { tx, tz } = structureFrame({ yaw }, up);
  const lots: SettlementLot[] = [];
  for (let i = 0; i < count; i++) {
    const along = (i - (count - 1) / 2) * SETTLEMENT.pitchPeople * person, side = i % 2 === 0 ? 1 : -1;
    const off = side * SETTLEMENT.setbackPeople * person;
    const dir = vecNormalize({ x: up.x + (tx.x * along + tz.x * off) / planet.radius, y: up.y + (tx.y * along + tz.y * off) / planet.radius, z: up.z + (tx.z * along + tz.z * off) / planet.radius });
    // the door is on the layout's south face (its −z at no turn): a lot on the lane's +z side faces the lane at the site's yaw, one on the −z side turned about
    lots.push({ builder: rng.int(0, BUILDERS - 1), dir, yaw: side > 0 ? yaw : yaw + Math.PI });
  }
  return { yaw, lots };
}

/** 74.5: what the site score and the kinds read of a planet's world sheet, per vertex (cached per sheet). */
interface SiteWorld {
  sheet: WorldSheet;
  /** in a lake or on a playa (the basin's floor under its spill), or touching one */
  wet: Uint8Array;
  /** hops to water (a chain of SETTLEMENT_WORLD.waterOrder and up, or a lake's or playa's edge), and the water vertex nearest by hops (−1: none); hops to a shore alone */
  hops: Int16Array; nearestWater: Int32Array; shoreHops: Int16Array;
  /** the basin's lowness, 0–1 */
  lowness: Float32Array;
  /** the crossings: the sheet's fords and passes */
  crossings: Array<{ vertex: number; kind: 'ford' | 'pass'; dir: Vec3 }>;
}
const worlds = new WeakMap<WorldSheet, SiteWorld>();

function siteWorld(sheet: WorldSheet): SiteWorld {
  const hit = worlds.get(sheet);
  if (hit) return hit;
  const g = sheet.graph, V = g.count;
  const under = new Uint8Array(V);   // a lake's or a playa's floor: its basin's vertices under the spill
  for (const b of sheet.basins) if (b.lake || b.playa) for (const v of b.vertices) if (sheet.radius[v] < b.spillRadius) under[v] = 1;
  const wet = new Uint8Array(V), shore: number[] = [];
  for (let v = 0; v < V; v++) {
    let touches = under[v] === 1, edge = false;
    for (let k = g.start[v]; k < g.start[v + 1]; k++) { if (under[g.nbr[k]]) touches = true; else if (under[v]) edge = true; }
    wet[v] = touches ? 1 : 0;
    if (edge) shore.push(v);
  }
  const chainV: number[] = [];
  for (const c of sheet.chains) if (c.order >= SETTLEMENT_WORLD.waterOrder) for (const v of c.vertices) if (!under[v]) chainV.push(v);
  const bfs = (sources: readonly number[], origin?: Int32Array): Int16Array => {
    const hops = new Int16Array(V).fill(32767), queue: number[] = [];
    for (const v of sources) if (hops[v] !== 0) { hops[v] = 0; if (origin) origin[v] = v; queue.push(v); }
    for (let q = 0; q < queue.length; q++) {
      const v = queue[q];
      for (let k = g.start[v]; k < g.start[v + 1]; k++) { const w = g.nbr[k]; if (hops[w] > hops[v] + 1) { hops[w] = hops[v] + 1; if (origin) origin[w] = origin[v]; queue.push(w); } }
    }
    return hops;
  };
  const nearestWater = new Int32Array(V).fill(-1);
  const hops = bfs([...shore, ...chainV].sort((a, b) => a - b), nearestWater), shoreHops = bfs(shore);
  const lowness = new Float32Array(V);
  for (let v = 0; v < V; v++) {
    const b = sheet.basins[sheet.basinOf[v]];
    lowness[v] = Number.isFinite(b.depth) ? Math.max(0, Math.min(1, (b.spillRadius - sheet.radius[v]) / Math.max(1e-9, b.depth))) : Math.max(0, Math.min(1, 1 - sheet.elevation[v]));
  }
  const crossings = sheet.candidates.filter(c => c.kind === 'ford' || c.kind === 'pass')
    .map(c => ({ vertex: c.vertex, kind: c.kind as 'ford' | 'pass', dir: { x: g.dirs[3 * c.vertex], y: g.dirs[3 * c.vertex + 1], z: g.dirs[3 * c.vertex + 2] } }));
  const w: SiteWorld = { sheet, wet, hops, nearestWater, shoreHops, lowness, crossings };
  worlds.set(sheet, w);
  return w;
}

/** A vertex's direction. */
const vdir = (g: WorldSheet['graph'], v: number): Vec3 => ({ x: g.dirs[3 * v], y: g.dirs[3 * v + 1], z: g.dirs[3 * v + 2] });

/**
 * 391 (74.3 step 2, the owner: "Bases move to shore"): the nearest dry ground to `dir` by the settlements' own rule — `dir`
 * itself where its vertex is neither in a lake nor touching one (a playa is dry salt: not refused), else the direction of the nearest vertex that
 * is not (by hops over the route graph, the closest by arc among the first hop's), a shore or the rise above it. Pure; the
 * sheet is the planet's as generated, so a crater moves nothing here.
 */
export function dryGroundNear(planet: PlanetState, dir: Vec3, ok?: (d: Vec3) => boolean): Vec3 {
  const sheet = worldSheetOf(planet), g = sheet.graph, wet = lakeShore(sheet);
  const start = g.nodeOf[nearestSurfaceVertex(planet, dir)];
  if (start < 0 || !wet[start]) return dir;
  return nearestDry(sheet, wet, start, dir, ok);
}
/** 391: is `dir` on a lake's floor or touching it (what a base refuses)? */
export function onLakeShore(planet: PlanetState, dir: Vec3): boolean {
  const sheet = worldSheetOf(planet);
  const v = sheet.graph.nodeOf[nearestSurfaceVertex(planet, dir)];
  return v >= 0 && lakeShore(sheet)[v] === 1;
}
/** The nearest vertex to `start` by hops that is dry (and passes `ok`, where given: the wheels' ground), the closest by arc among its ring's. */
function nearestDry(sheet: WorldSheet, wet: Uint8Array, start: number, dir: Vec3, ok?: (d: Vec3) => boolean): Vec3 {
  const g = sheet.graph;
  const seen = new Uint8Array(g.count); seen[start] = 1;
  let ring = [start];
  while (ring.length) {
    const next: number[] = [];
    let best = -1, bestArc = Infinity;
    for (const v of ring) for (let k = g.start[v]; k < g.start[v + 1]; k++) {
      const w = g.nbr[k];
      if (seen[w]) continue;
      seen[w] = 1; next.push(w);
      if (!wet[w]) { const d = vdir(g, w); if (ok && !ok(d)) continue; const a = arc(dir, d); if (a < bestArc) { bestArc = a; best = w; } }
    }
    if (best >= 0) return vdir(g, best);
    ring = next;
  }
  return dir;   // (a world all water: nowhere dry)
}
/** 391: a lake's floor (its basin's vertices under the spill) and the ring touching it — the lakes alone: a playa is dry salt, a base stands on it (pallor's bases stay bit for bit). Cached per sheet. */
const lakeShores = new WeakMap<WorldSheet, Uint8Array>();
function lakeShore(sheet: WorldSheet): Uint8Array {
  const hit = lakeShores.get(sheet);
  if (hit) return hit;
  const g = sheet.graph, V = g.count, under = new Uint8Array(V), wet = new Uint8Array(V);
  for (const b of sheet.basins) if (b.lake) for (const v of b.vertices) if (sheet.radius[v] < b.spillRadius) under[v] = 1;
  for (let v = 0; v < V; v++) {
    let touches = under[v] === 1;
    for (let k = g.start[v]; k < g.start[v + 1] && !touches; k++) if (under[g.nbr[k]]) touches = true;
    wet[v] = touches ? 1 : 0;
  }
  lakeShores.set(sheet, wet);
  return wet;
}
/** `d` along the ground at `up` (the part across it removed), or null where it has none. */
function along(up: Vec3, d: Vec3): Vec3 | null {
  const t = vecSub(d, vecScale(up, vecDot(d, up)));
  return Math.sqrt(vecDot(t, t)) > 1e-12 ? vecNormalize(t) : null;
}
/** A chain's way at vertex `v` (downhill, or from the vertex above where it ends), along the ground at `up`. */
function chainWay(sheet: WorldSheet, v: number, up: Vec3): Vec3 | null {
  const g = sheet.graph, down = sheet.downhill[v];
  if (down >= 0) return along(up, vecSub(vdir(g, down), vdir(g, v)));
  for (let k = g.start[v]; k < g.start[v + 1]; k++) if (sheet.downhill[g.nbr[k]] === v) return along(up, vecSub(vdir(g, v), vdir(g, g.nbr[k])));
  return null;
}

/**
 * 74.5: a site's kind by where it stands, and its lane's way: its own crossing near (a ford village across the chain there;
 * a pass hamlet through the col, down its steepest side), a shore near (a lakeshore hamlet along it: across the way to the
 * water), high and dry (a ridge post), else a valley village — along its chain's bank where one is near, else keyed.
 */
function siteKind(w: SiteWorld, v: number, dir: Vec3, crossing: SiteWorld['crossings'][number] | null, personArc: number): { kind: SettlementKind; lane: Vec3 | null } {
  const up = vecNormalize(dir), sheet = w.sheet, g = sheet.graph;
  if (crossing && arc(dir, crossing.dir) <= SETTLEMENT_WORLD.kindCrossingPeople * personArc) {
    if (crossing.kind === 'ford') {
      const way = chainWay(sheet, crossing.vertex, up);
      return { kind: 'fordVillage', lane: way ? vecNormalize({ x: up.y * way.z - up.z * way.y, y: up.z * way.x - up.x * way.z, z: up.x * way.y - up.y * way.x }) : null };
    }
    return { kind: 'passHamlet', lane: sheet.downhill[crossing.vertex] >= 0 ? along(up, vecSub(vdir(g, sheet.downhill[crossing.vertex]), crossing.dir)) : null };
  }
  const water = w.nearestWater[v];
  if (w.shoreHops[v] <= SETTLEMENT_WORLD.kindShoreHops && water >= 0) {
    const toWater = along(up, vecSub(vdir(g, water), dir));
    return { kind: 'lakeshoreHamlet', lane: toWater ? vecNormalize({ x: up.y * toWater.z - up.z * toWater.y, y: up.z * toWater.x - up.x * toWater.z, z: up.x * toWater.y - up.y * toWater.x }) : null };
  }
  if (sheet.elevation[v] >= SETTLEMENT_WORLD.ridgeAt && w.hops[v] >= SETTLEMENT_WORLD.waterHops) return { kind: 'ridgePost', lane: null };
  return { kind: 'valleyVillage', lane: water >= 0 && w.hops[v] <= SETTLEMENT_WORLD.kindChainHops && w.shoreHops[v] > w.hops[v] ? chainWay(sheet, water, up) : null };
}

/** The planets a match is fought on: where its people stand. */
function battlePlanets(state: Pick<GameState, 'players'>): number[] {
  return [...new Set(state.players.map(p => p.planetIndex))].sort((a, b) => a - b);
}

/**
 * The setup pass: the settlements the board bounds (with a board, those within SETTLEMENT.boardReach of the bases'
 * separation of each base; without one, the best), at most SETTLEMENT.maxPlaced, laid as neutral `house` structures —
 * one a lot, its builder the lot's layout, its yaw the lot's. The same state when the switch is off or nothing is laid.
 */
export function withSettlements(state: GameState): GameState {
  if (!state.config.settlements || !buildingsByFunction(state.config)) return state;
  const extra: StructureState[] = [];
  for (const planetIndex of battlePlanets(state)) {
    const sites = planetSettlements(state, planetIndex);
    if (!sites.length) continue;
    const bases = state.board?.bases.filter(b => b.planetIndex === planetIndex).map(b => vecNormalize(b.dir)) ?? [];
    let chosen = sites.slice();
    if (bases.length >= 2) {
      const sep = arc(bases[0], bases[1]);
      chosen = chosen.filter(s => bases.every(b => arc(s.dir, b) <= SETTLEMENT.boardReach * sep));
    }
    chosen.sort((a, b) => b.score - a.score || a.id - b.id);
    for (const s of chosen.slice(0, SETTLEMENT.maxPlaced)) for (const lot of s.lots) {
      extra.push({ id: state.structures.length + extra.length, type: 'house', planetIndex, surfaceDir: lot.dir, yaw: lot.yaw, health: structureHealth('house'), ownerId: -1, builder: lot.builder });
    }
  }
  return extra.length ? { ...state, structures: [...state.structures, ...extra] } : state;
}
